import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    FormControl,
    FormControlLabel,
    Checkbox,
    Grid,
    Autocomplete,
    TextField as MUITextField
} from '@mui/material';
import { Colors } from '../../../../../styles/theme';
import { InvoiceService } from '../../../../../service/invoiceService';
import { toast } from 'react-toastify';

const TransferInvoiceDialog = ({
    open = false,
    onClose,
    invoiceData,
    onTransferSuccess,
    onRefresh,
    hasPayments = false, // Indicates if original invoice has payments
    hasDiscounts = false, // Indicates if original invoice has discounts
    hasInsurance = false // Indicates if original invoice has insurance claims
}) => {
    const [transferOptions, setTransferOptions] = useState({
        transferItems: true,
        transferPayments: false,
        transferDiscounts: false,
        transferInsurance: false,
        putBackToStock: false
    });
    const [inProgress, setInProgress] = useState(false);
    const [reasons, setReasons] = useState([]); // available reasons from backend
    const [selectedReasons, setSelectedReasons] = useState([]); // selected reason objects

    // dont' need this since stock return can be done even without item transfer (fresh replacement flow)
    // const hasStockBackedItems = React.useMemo(() => {
    //     const items = invoiceData?.items || [];
    //     return items.some((item) => {
    //         if (!item?.itemId) return false;
    //         if (item.type === 'frame') {
    //             return item.frameHandling === 'stock';
    //         }
    //         if (item.type === 'contact_lens') {
    //             return false;
    //         }
    //         return true;
    //     });
    // }, [invoiceData]);

    // Load transfer reasons on open
    useEffect(() => {
        if (!open) return;
        let mounted = true;

        InvoiceService.getTransferReasons()
            .then(resp => {
                const { code, data } = resp;
                if (code !== 0) {
                    toast.error('Failed to load transfer reasons');
                    return;
                }
                if (mounted && data) {
                    setReasons(data);
                }
            })
            .catch(() => {
                // Non-blocking: proceed without reasons if load fails
            });
        return () => { mounted = false; };
    }, [open]);

    const handleOptionChange = (optionName, checked) => {
        setTransferOptions(prev => ({
            ...prev,
            [optionName]: checked
        }));
    };

    const handleTransferConfirm = async () => {
        try {
            setInProgress(true);

            const response = await InvoiceService.transferToNewInvoice(invoiceData.id, {
                ...transferOptions,
                transferReasons: selectedReasons.map(r => r.id)
            });

            if (response.code !== 0) {
                console.error('Error transferring invoice:', response.message);
                toast.error(response.message || 'Error transferring invoice');
                return;
            }

            // Create a summary of what was transferred
            const transferredItems = [];
            if (transferOptions.transferItems) transferredItems.push('Items');
            if (transferOptions.transferPayments) transferredItems.push('Payments');
            if (transferOptions.transferDiscounts) transferredItems.push('Discounts');
            if (transferOptions.transferInsurance) transferredItems.push('Insurance');
            if (transferOptions.putBackToStock) transferredItems.push('Stock Returned');

            const transferSummary = transferredItems.length > 0
                ? ` (Transferred: ${transferredItems.join(', ')})`
                : ' (No components transferred)';

            // Show success message with new invoice details
            toast.success(
                `Invoice transferred successfully! New invoice ${response.data.newInvoice.invoiceNumber} has been created.${transferSummary} Loading new invoice...`,
                { autoClose: 5000 }
            );

            // Refresh parent invoice list if callback provided
            if (onRefresh) {
                onRefresh();
            }

            // Close this dialog
            onClose();

            // Open the new invoice if callback provided
            if (onTransferSuccess) {
                // Use setTimeout to ensure the current dialog closes first
                setTimeout(() => {
                    onTransferSuccess(response.data.newInvoice);
                }, 100);
            }

        } catch (error) {
            console.error('Transfer failed:', error);
            toast.error('Failed to transfer invoice: ' + (error.message || 'Unknown error'));
        } finally {
            setInProgress(false);
        }
    };

    const handleCancel = () => {
    setTransferOptions({
            transferItems: true,
            transferPayments: false,
            transferDiscounts: false,
            transferInsurance: false,
            putBackToStock: false
        });
    setSelectedReasons([]);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ background: Colors.primary, color: Colors.white }}>
                Transfer Invoice Options
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                
                <Typography variant="body1" sx={{ mb: 3 }}>
                    Select which items to transfer from invoice {invoiceData?.invoiceNumber} to the new invoice:
                </Typography>

                <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={transferOptions.transferItems}
                                        onChange={(e) => handleOptionChange('transferItems', e.target.checked)}
                                    />
                                }
                                label="Invoice Items (products, services, frames, lenses, etc.)"
                            />
                        </Grid>
                        {(
                            <Grid item xs={12} sx={{ pl: 4 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={transferOptions.putBackToStock}
                                            onChange={(e) => handleOptionChange('putBackToStock', e.target.checked)}
                                        />
                                    }
                                    label="Put back to stock (restore transferred stock items to inventory)"
                                />
                            </Grid>
                        )}
                        {/* Only show payments checkbox if original invoice has payments */}
                        {hasPayments && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={transferOptions.transferPayments}
                                            onChange={(e) => handleOptionChange('transferPayments', e.target.checked)}
                                        />
                                    }
                                    label="Payments"
                                />
                            </Grid>
                        )}
                        {/* Only show discounts checkbox if original invoice has discounts */}
                        {hasDiscounts && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={transferOptions.transferDiscounts}
                                            onChange={(e) => handleOptionChange('transferDiscounts', e.target.checked)}
                                        />
                                    }
                                    label="Discounts & Promotions"
                                />
                            </Grid>
                        )}
                        {/* Only show insurance checkbox if original invoice has insurance claims */}
                        {hasInsurance && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={transferOptions.transferInsurance}
                                            onChange={(e) => handleOptionChange('transferInsurance', e.target.checked)}
                                        />
                                    }
                                    label="Insurance Claims"
                                />
                            </Grid>
                        )}
                    </Grid>
                </FormControl>

                {/* Transfer Reasons multi-select */}
                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Transfer Reasons</Typography>
                <Autocomplete
                    multiple
                    options={reasons}
                    getOptionLabel={(option) => option.description || option.code}
                    value={selectedReasons}
                    onChange={(_, newValue) => setSelectedReasons(newValue)}
                    renderInput={(params) => (
                        <MUITextField {...params} label="Select one or more reasons" placeholder="Reasons" />
                    )}
                    disableCloseOnSelect
                />

                <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                    The new invoice will start with a fresh status and new invoice number.
                    This action cannot be undone.
                </Typography>
                {(
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Tip: If customer changed their mind and you are creating a fresh invoice, keep "Invoice Items" unchecked and enable "Put back to stock".
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleTransferConfirm}
                    color="warning"
                    disabled={inProgress}
                >
                    {inProgress ? 'Transferring...' : 'Transfer Invoice'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TransferInvoiceDialog;
